import { IInvoiceDetail, IInvoiceDetailPrice, InvoiceDetailItem } from './IInvoiceDetail';
import { IFileResponse } from '@lib/core';
import { InvoiceItemGroup } from './InvoiceItemGroup';
import {
    IInvoiceAddressDto,
    IInvoiceHistory,
    IInvoiceItemOrderRelationViewDto,
    IInvoiceItemReceiptRelationViewDto,
    InvoiceHistory,
    IStandaloneInvoiceDetailDto,
} from './IStandaloneInvoiceDetail';
import { signal, WritableSignal } from '@angular/core';
import { ICompanyAddressDto } from '@lib/address';
import { InvoiceStatus } from '@lib/invoice-public';
import { InvoicePossibleActionType } from './InvoicePossibleActionType';
import { ListOrderStatus } from '../../list/types/ListOrderStatus';
import { CreateInvoiceCharge } from '../../create-standalone/types/StandaloneInvoiceDraft';
import { ILinkedDocumentDto, LinkedDocument } from './LinkedDocument';
import { LinkedDocumentType } from './LinkedDocumentType';
import { InvoiceDocumentType } from '../../types/InvoiceDocumentType';

export class InvoiceDetail {
    public static fromStandalone(dto: IStandaloneInvoiceDetailDto): InvoiceDetail {
        // Convert standalone items to invoice detail items
        const items: InvoiceDetailItem[] = dto.items.map((item) => {
            const orderErpNumber = InvoiceDetail.normalizeOrderNumber(item.orderRelation?.orderErpNumber);

            const detailItem = new InvoiceDetailItem({
                itemId: item.id,
                invoicePosition: item.position,
                orderPosition: item.orderRelation ? item.orderRelation.orderItemPosition : null,
                receiptPosition: item.receiptRelation ? item.receiptRelation.receiptPosition : null,
                name: item.name,
                description: item.description,
                articleNumberSupplier: item.supplierArticleNumber,
                price: {
                    unitPrice: item.unitPrice?.amount ?? null,
                    linePrice: item.netPrice?.amount ?? null,
                    currency: item.netPrice?.currency ?? null,
                    taxes: item.tax,
                },
                quantity: item.quantity?.amount,
                orderUnit: item.quantity?.unit,
                discountPercentage: item.discountPercentage ?? 0,
                priceQuantity: item.priceQuantity ?? 1,
                materialGroupCode: item.materialGroupCode ?? null,
                orderId: item.orderRelation ? item.orderRelation.orderId : null,
                orderErpNumber,
                receiptNumber: item.receiptRelation ? item.receiptRelation.receiptNumber : null,
                receiptId: item.receiptRelation ? item.receiptRelation.receiptId : null,
                orderItemQuantities: item.orderItemQuantities ?? null,
            });

            if (item.accounting) {
                detailItem.setAccounting(item.accounting);
            }

            return detailItem;
        });

        // Calculate total prices and taxes
        const totalNetPrice = dto.price?.totalNetPrice ?? null;

        const totalGrossPrice = dto.price?.totalGrossPrice ?? null;

        // Create the invoice detail object
        const linkedDocuments: LinkedDocument[] =
            dto.linkedDocuments?.map((doc) => InvoiceDetail.mapLinkedDocument(doc)) ?? [];

        const detail = new InvoiceDetail(
            {
                id: dto.id,
                invoiceNumber: dto.invoiceNumber,
                companyCodeId: dto.companyCodeId,
                supplierName: dto.supplier?.companyAddress.companyName.line1,
                supplierNumber: dto.supplier?.supplierNumber,
                supplierAddress: dto.supplier?.companyAddress,
                invoiceAddress: dto.invoiceAddress,
                bookingDate: dto.createDate,
                documentDate: dto.invoiceDate,
                comment: dto.remark,
                status: dto.status,
                documentType: dto.documentType,
                files: dto.files,
                items: items,
                price: {
                    totalNetPrice: totalNetPrice,
                    totalGrossPrice: totalGrossPrice,
                    taxes: dto.price?.taxes ?? null,
                    currency: dto.price?.currency ?? null,
                },
                paymentUntil: dto.paymentUntil,
                paymentDate: dto.paymentDate,
                charges: dto.charges,
                possibleActions: dto.possibleActions,
                linkedDocuments,
            },
            items,
        );

        detail.messageToBuyer = dto.messageToBuyer;
        detail.remark = dto.remark;
        detail.history.set(dto.history);

        for (const item of dto.items) {
            if (item.orderRelation) {
                detail.addOrderRelation(item.orderRelation);
            }
            if (item.receiptRelation) {
                detail.addReceiptRelation(item.receiptRelation);
            }
        }

        return detail;
    }

    bookingDate: string;
    documentDate: string;
    files: IFileResponse[];
    id: string;
    invoiceNumber: string;
    companyCodeId?: string;
    itemGroups: InvoiceItemGroup<InvoiceDetailItem>[];
    note: string;
    price: IInvoiceDetailPrice;
    charges: CreateInvoiceCharge[];
    status: InvoiceStatus;
    documentType: InvoiceDocumentType;
    isCreditNote: boolean;
    supplierName: string;
    supplierNumber?: string | null;
    supplierAddress: ICompanyAddressDto;
    invoiceAddress: IInvoiceAddressDto;
    paymentUntil: string;
    paymentDate: string;
    remark: string;
    messageToBuyer: string;
    possibleActions: InvoicePossibleActionType[] = [];
    history: WritableSignal<IInvoiceHistory>;
    linkedDocuments: LinkedDocument[] = [];

    private readonly _orderRelations: IInvoiceItemOrderRelationViewDto[] = [];
    private readonly _receiptRelations: IInvoiceItemReceiptRelationViewDto[] = [];

    constructor(dto: IInvoiceDetail, items: InvoiceDetailItem[]) {
        this.id = dto.id;
        this.invoiceNumber = dto.invoiceNumber;
        this.companyCodeId = dto.companyCodeId;
        this.supplierName = dto.supplierName;
        this.supplierNumber = dto.supplierNumber;
        this.supplierAddress = dto.supplierAddress;
        this.invoiceAddress = dto.invoiceAddress;
        this.bookingDate = dto.bookingDate;
        this.documentDate = dto.documentDate;
        this.note = dto.comment;
        this.status = dto.status;
        // A missing type reads as an ordinary invoice
        this.documentType = dto.documentType ?? InvoiceDocumentType.INVOICE;
        this.isCreditNote = this.documentType === InvoiceDocumentType.CREDIT_NOTE;
        this.files = dto.files;
        this.items = items;
        this.price = dto.price;
        this.paymentUntil = dto.paymentUntil;
        this.paymentDate = dto.paymentDate;
        this.charges = dto.charges;
        this.possibleActions = dto.possibleActions || [];
        this.history = signal(new InvoiceHistory([]));
        this.linkedDocuments = dto.linkedDocuments || [];
    }

    public addOrderRelation(orderRelation: IInvoiceItemOrderRelationViewDto): void {
        if (!this._orderRelations.some((relation) => relation.orderId === orderRelation.orderId)) {
            const fullRelation: IInvoiceItemOrderRelationViewDto = {
                ...orderRelation,
                orderErpNumber: InvoiceDetail.normalizeOrderNumber(orderRelation.orderErpNumber),
                supplierName: orderRelation.supplierName || this.supplierName,
                status: convertOrderStatus(orderRelation.status.toString()),
                orderDate: orderRelation.orderDate || '',
            };
            this._orderRelations.push(fullRelation);
        }
    }

    public get items(): InvoiceDetailItem[] {
        return this.itemGroups.reduce((acc, group) => acc.concat(group.items), []);
    }

    public set items(items: InvoiceDetailItem[]) {
        this.itemGroups = InvoiceItemGroup.groupItems(items);
    }

    public get orderRelations(): IInvoiceItemOrderRelationViewDto[] {
        return this._orderRelations.slice();
    }

    public addReceiptRelation(receiptRelation: IInvoiceItemReceiptRelationViewDto): void {
        if (!this._receiptRelations.some((relation) => relation.receiptId === receiptRelation.receiptId)) {
            const fullRelation: IInvoiceItemReceiptRelationViewDto = {
                ...receiptRelation,
                isOpen: receiptRelation.isOpen,
                inApproval: receiptRelation.inApproval,
                deliveryDate: receiptRelation.deliveryDate || '',
                creationDate: receiptRelation.creationDate || '',
            };
            this._receiptRelations.push(fullRelation);
        }
    }

    public get receiptRelations(): IInvoiceItemReceiptRelationViewDto[] {
        return this._receiptRelations.slice();
    }

    private static normalizeOrderNumber(orderNumber?: string | null): string | null {
        if (!orderNumber) {
            return null;
        }

        const trimmedOrderNumber = orderNumber.trim();
        return trimmedOrderNumber.length > 0 ? trimmedOrderNumber : null;
    }

    /**
     * Update the files of the invoice detail
     * @param updatedFilesList
     * @return - A new instance of the invoice detail with the updated files
     */
    public updateFiles(updatedFilesList: IFileResponse[]): InvoiceDetail {
        const detail = new InvoiceDetail(
            {
                ...this.toDto(),
                files: updatedFilesList.slice(),
            },
            this.items.map((i) => i.clone()),
        );

        detail.history.set(this.history());

        return detail;
    }

    private toDto(): IInvoiceDetail {
        return {
            id: this.id,
            invoiceNumber: this.invoiceNumber,
            supplierName: this.supplierName,
            bookingDate: this.bookingDate,
            documentDate: this.documentDate,
            comment: this.note,
            status: this.status,
            documentType: this.documentType,
            files: this.files,
            items: this.itemGroups.reduce((acc, group) => acc.concat(group.items), []),
            price: this.price,
            paymentUntil: this.paymentUntil,
            paymentDate: this.paymentDate,
            invoiceAddress: this.invoiceAddress,
            supplierAddress: this.supplierAddress,
            charges: this.charges,
            possibleActions: this.possibleActions,
            linkedDocuments: this.linkedDocuments,
        };
    }

    private static mapLinkedDocument(dto: ILinkedDocumentDto): LinkedDocument {
        const metaData = dto.metaData as Record<string, unknown> | undefined;
        const documentNumber = (metaData?.['documentNumber'] as string) ?? dto.documentNumber;
        const erpOrderNumber = metaData?.['erpOrderNumber'] as string | undefined;
        return new LinkedDocument({
            documentId: dto.documentId,
            documentType: dto.documentType as LinkedDocumentType,
            documentTypeName: dto.documentTypeName ?? dto.documentType,
            documentNumber: documentNumber ?? erpOrderNumber,
            addedAt: dto.addedAt,
            addedBy: dto.addedBy,
            navigationAllowed: dto.navigationAllowed ?? false,
            metaData,
        });
    }
}

function convertOrderStatus(status: string): ListOrderStatus {
    switch (status) {
        case 'ORDERED':
            return ListOrderStatus.ORDERED;
        case 'NEEDS_APPROVAL':
            return ListOrderStatus.NEEDS_APPROVAL;
        case 'REJECTED':
            return ListOrderStatus.REJECTED;
        case 'DELIVERED':
            return ListOrderStatus.DELIVERED;
        case 'PARTLY_DELIVERED':
            return ListOrderStatus.PARTLY_DELIVERED;
        case 'COMPLETED':
            return ListOrderStatus.COMPLETED;
        case 'CANCELED':
            return ListOrderStatus.CANCELED;
        case 'INVOICED':
            return ListOrderStatus.INVOICED;
        case 'INTERNAL_PROCESSING':
            return ListOrderStatus.INTERNAL_PROCESSING;
        case 'ANSWER_RECEIVED':
            return ListOrderStatus.ANSWER_RECEIVED;
        case 'REJECTED_DURING_INTERNAL_PROCESSING':
            return ListOrderStatus.REJECTED_DURING_INTERNAL_PROCESSING;
        case 'CONFIRMED':
            return ListOrderStatus.CONFIRMED;
        case 'TRANSFERRED':
            return ListOrderStatus.TRANSFERRED;
        case 'TO_BE_RELEASED':
            return ListOrderStatus.TO_BE_RELEASED;
        default:
            return ListOrderStatus.ORDERED;
    }
}
